import React from 'react';
import { Monitor, Package, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { Lesson } from '../../../types';

interface MaterialsSectionProps {
  formData: Partial<Lesson>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Lesson>>>;
}

export const MaterialsSection: React.FC<MaterialsSectionProps> = ({
  formData,
  setFormData
}) => {
  const { t } = useTranslation();
  const addDigitalMaterial = () => {
    setFormData(prev => ({
      ...prev,
      materials: {
        ...prev.materials!,
        digital: [...(prev.materials?.digital || []), '']
      }
    }));
  };

  const updateDigitalMaterial = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      materials: {
        ...prev.materials!,
        digital: prev.materials?.digital?.map((item, i) => i === index ? value : item) || []
      }
    }));
  };

  const removeDigitalMaterial = (index: number) => {
    setFormData(prev => ({
      ...prev,
      materials: {
        ...prev.materials!,
        digital: prev.materials?.digital?.filter((_, i) => i !== index) || []
      }
    }));
  };

  const addPhysicalMaterial = () => {
    setFormData(prev => ({
      ...prev,
      materials: {
        ...prev.materials!,
        physical_alternatives: [...(prev.materials?.physical_alternatives || []), '']
      }
    }));
  };

  const updatePhysicalMaterial = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      materials: {
        ...prev.materials!,
        physical_alternatives: prev.materials?.physical_alternatives?.map((item, i) => i === index ? value : item) || []
      }
    }));
  };

  const removePhysicalMaterial = (index: number) => {
    setFormData(prev => ({
      ...prev,
      materials: {
        ...prev.materials!,
        physical_alternatives: prev.materials?.physical_alternatives?.filter((_, i) => i !== index) || []
      }
    }));
  };

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Package className="w-5 h-5 mr-2" />
        {t('materials.materialsAndResources')}
      </h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Digital Materials */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center">
            <Monitor className="w-4 h-4 mr-2" />
            {t('materials.digitalMaterials')}
          </h3>
          
          <div className="space-y-3">
            {formData.materials?.digital?.map((material, index) => (
              <div key={index} className="flex items-center space-x-3">
                <input
                  type="text"
                  value={material}
                  onChange={(e) => updateDigitalMaterial(index, e.target.value)}
                  className="input-field flex-1"
                  placeholder={`${t('materials.digitalMaterial')} ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeDigitalMaterial(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            
            <button
              type="button"
              onClick={addDigitalMaterial}
              className="btn-secondary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>{t('materials.addDigitalMaterial')}</span>
            </button>
          </div>
        </div>

        {/* Physical Materials */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center">
            <Package className="w-4 h-4 mr-2" />
            {t('materials.physicalMaterials')}
          </h3>
          
          <div className="space-y-3">
            {formData.materials?.physical_alternatives?.map((material, index) => (
              <div key={index} className="flex items-center space-x-3">
                <input
                  type="text"
                  value={material}
                  onChange={(e) => updatePhysicalMaterial(index, e.target.value)}
                  className="input-field flex-1"
                  placeholder={`${t('materials.physicalMaterial')} ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removePhysicalMaterial(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            
            <button
              type="button"
              onClick={addPhysicalMaterial}
              className="btn-secondary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>{t('materials.addPhysicalMaterial')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
